import {Injectable} from '@angular/core';
import {NgxsAfterBootstrap, NgxsSimpleChange, State} from '@ngxs/store';
import {StoreModel} from 'src/app/_models/store.model';
import {HelperService} from 'src/app/_services/helper.service';
import {RealTimeService} from 'src/app/_services/real-time-connection/real-time.service';
import StoreEntity from 'src/app/_entities/stores/store.entity';
import {StoresService} from 'src/app/_services/stores.service';
import {Computed, DataAction, StateRepository} from '@angular-ru/ngxs/decorators';
import {NgxsDataEntityCollectionsRepository} from '@angular-ru/ngxs/repositories';
import {createEntityCollections, EntityIdType} from '@angular-ru/cdk/entity';
import BaseEntityCollectionsOptions from '../base-entity-collections-options';
import EntityMap from '../../_builders/entity-map';
import {STORE_TYPES} from '../../_enums/stores/store-type.enum';

@StateRepository()
@State({
    name: 'stores',
    defaults: {
        ...createEntityCollections(),
        loading: false
    }
})
@Injectable()
export default class StoresEntitiesState
    extends NgxsDataEntityCollectionsRepository<StoreModel, EntityIdType, BaseEntityCollectionsOptions>
    implements NgxsAfterBootstrap {

    /**
     * Every listener, not one. A single slot meant each registration silently cancelled the one
     * before it: the listing wizard alone has three steps that watch the store list, and so do
     * the orders views, the settlements report and the FBA product picker. The cancelled
     * components kept an empty store list for the life of the page, which is how a store that is
     * connected came to fail the "Store connected" readiness check and show as "#1".
     */
    private _listeners = new Set<(state?: StoresEntitiesState) => void>();
    private _changeObserver: typeof this.state$;

    constructor(
        private realtimeService: RealTimeService,
        private helperService: HelperService,
        private storeService: StoresService,
    ) {
        super();
    }

    ngxsOnChanges(_?: NgxsSimpleChange) {
        super.ngxsOnChanges(_);
        if (this.isInitialised && !this._changeObserver) {
            this._changeObserver = this.state$;
            this._changeObserver.subscribe(() => this._listeners.forEach(listener => listener(this)));
        }
    }

    // Computed
    @Computed()
    public get storeEntities() {
        return this.entitiesArray
            .map(store => new StoreEntity(store));
    }

    @Computed()
    public get storesEntityMap(): EntityMap<StoreEntity> {
        return new EntityMap<StoreEntity>(this.ids
            .reduce((results, id) => {
                results[id] = new StoreEntity(this.entities[id]);
                return results;
            }, {}));
    }

    @Computed()
    public get byType() {
        return (type: typeof STORE_TYPES[number]) =>
            this.storeEntities
                .filter(store => store.type?.toLowerCase() === type.toLowerCase());

    }

    @Computed()
    public get pathToImage() {
        return (id: number) => {
            return `assets/media/icons/${this.entities[id]?.type.toLowerCase()}-icon.svg`;
        };
    }

    ngxsAfterBootstrap() {
        this.helperService.onUserIsActive(() => this.load());
    }

    // Action
    @DataAction()
    load() {
        this.patchState({loading: true});
        this.storeService.fetch().subscribe(stores => {
            this.setEntitiesAll(stores);
            this.patchState({ loading: false, loaded: true });
        });
    }

    @DataAction()
    lazyLoad() {
        !this.snapshot.loading &&
        !this.snapshot.loaded &&
        this.load();
    }

    /**
     * Registers the callback against every later state change and returns the handle that removes
     * it again. Callers that only want the list once should use that handle - see
     * StoresManager.getAll, which drops its listener as soon as the list arrives.
     */
    public onStateChange(callback: (state?: StoresEntitiesState) => void): () => void {
        this._listeners.add(callback);
        if (!this.snapshot.loading) {
            callback(this);
        }
        return () => this._listeners.delete(callback);
    }
}
